import { Add, DeleteOutlined } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    type CriticalityConfig,
    type CriticalityRule,
    compileCriticality,
    compileRule,
    decompileCriticality,
    decompileRule,
} from '../condition-mapper';
import type { DataCriticality, FieldCondition } from '../product-requirements-config.dto';
import ConditionBuilder, { type FieldOption } from './ConditionBuilder';

const CRITICALITIES: DataCriticality[] = ['mandatory', 'important', 'optional', 'irrelevant'];

interface Props {
    value: FieldCondition | null;
    /** The static fallback criticality this override would replace conditionally. */
    staticCriticality: DataCriticality;
    fieldOptions: FieldOption[];
    onChange: (value: FieldCondition | null) => void;
}

/**
 * Optional conditional override of a field's criticality: an ordered list of
 * "when <condition> ⇒ <criticality>" rules with a fallback. Off by default —
 * the static criticality stands until a PM turns this on.
 */
export default function CriticalityRulesEditor({ value, staticCriticality, fieldOptions, onChange }: Props) {
    const { t } = useTranslation();

    // Lazy init: decompile once. A null seed means the existing override is too
    // advanced to represent visually (shown read-only).
    const [seed] = useState(() => decompileCriticality(value, staticCriticality));
    const advanced = seed == null;
    const [config, setConfig] = useState<CriticalityConfig>(() => seed ?? { rules: [], fallback: staticCriticality });

    function emit(next: CriticalityConfig) {
        setConfig(next);
        onChange(compileCriticality(next));
    }

    if (advanced) {
        return (
            <Alert severity="info">
                {t('This criticality override is too advanced to edit visually and will be kept as-is.')}
            </Alert>
        );
    }

    const enabled = config.rules.length > 0;

    return (
        <Box>
            <FormControlLabel
                control={
                    <Switch
                        checked={enabled}
                        onChange={(e) =>
                            emit(
                                e.target.checked
                                    ? {
                                          rules: [{ when: { mode: 'all', conditions: [] }, criticality: 'mandatory' }],
                                          fallback: staticCriticality,
                                      }
                                    : { rules: [], fallback: staticCriticality },
                            )
                        }
                    />
                }
                label={t('Override criticality based on conditions')}
            />

            {enabled && (
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {config.rules.map((rule, index) => (
                        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
                                <Typography variant="body2">{t('When')}</Typography>
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel>{t('Set criticality to')}</InputLabel>
                                    <Select
                                        label={t('Set criticality to')}
                                        value={rule.criticality}
                                        onChange={(e) => {
                                            const rules = [...config.rules];
                                            rules[index] = { ...rule, criticality: e.target.value as DataCriticality };
                                            emit({ ...config, rules });
                                        }}
                                    >
                                        {CRITICALITIES.map((c) => (
                                            <MenuItem key={c} value={c}>
                                                {t(c)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        emit({ ...config, rules: config.rules.filter((_, i) => i !== index) })
                                    }
                                    aria-label={t('Remove rule')}
                                >
                                    <DeleteOutlined fontSize="small" />
                                </IconButton>
                            </Stack>
                            <ConditionBuilder
                                title={t('Conditions')}
                                value={compileRule(rule.when)}
                                fieldOptions={fieldOptions}
                                onChange={(node) => {
                                    const rules = [...config.rules];
                                    rules[index] = {
                                        ...rule,
                                        when: node
                                            ? (decompileRule(node) ?? { mode: 'all', conditions: [] })
                                            : { mode: 'all', conditions: [] },
                                    } as CriticalityRule;
                                    emit({ ...config, rules });
                                }}
                            />
                        </Paper>
                    ))}

                    <Box>
                        <Button
                            size="small"
                            startIcon={<Add />}
                            onClick={() =>
                                emit({
                                    ...config,
                                    rules: [
                                        ...config.rules,
                                        { when: { mode: 'all', conditions: [] }, criticality: 'mandatory' },
                                    ],
                                })
                            }
                        >
                            {t('Add criticality rule')}
                        </Button>
                    </Box>

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>{t('Otherwise (fallback)')}</InputLabel>
                        <Select
                            label={t('Otherwise (fallback)')}
                            value={config.fallback}
                            onChange={(e) => emit({ ...config, fallback: e.target.value as DataCriticality })}
                        >
                            {CRITICALITIES.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {t(c)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            )}
        </Box>
    );
}
